import SEO from "../../../components/seo.jsx"
import { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import HeroImg from "../../../assets/about-transplant.png";
import BreadcrumbImg from "../../../assets/breadcrumb-bg.jpg";
import ProcedureImg from "../../../assets/breadcrumb-bg.jpg";
import HistoryImg from "../../../assets/history-clinic.jpg";
import IndiaImg from "../../../assets/india-facility.jpeg";


gsap.registerPlugin(ScrollTrigger);
const cinzel = { fontFamily: "'Cinzel', serif" };

const PAGE = {

  hero: {
    eyebrow: "The Procedure",
    heading: "About Hair Transplant",
    sub: "A permanent, natural-looking solution to hair loss refined over seven decades of surgical innovation.",
  },

  breadcrumb: [
    { label: "Home", href: "/" },
    { label: "About Hair Transplant", href: null },
  ],

  intro: {
    eyebrow: "Overview",
    heading: "What Is a Hair Transplant?",
    paragraphs: [
      "Hair transplant is a surgical procedure that involves moving hair follicles from one part of the body usually the back or sides of the scalp to the balding or thinning areas. It's primarily used to address male pattern baldness or thinning hair in both men and women.",
      "There are two main techniques: follicular unit transplantation (FUT) and follicular unit extraction (FUE). FUT involves removing a strip of scalp, dissecting it into grafts, and transplanting them. FUE extracts individual follicular units directly from the scalp. The transplanted hair grows naturally, offering a permanent solution to hair loss in targeted areas.",
    ],
  },

  history: {
    eyebrow: "Since 1952",
    heading: "History of Hair Transplants",
    paragraphs: [
      "Hair transplants have evolved significantly since their inception in the 1950s. Dr. Norman Orentreich performed the first modern hair transplant in 1952, introducing the concept of donor dominance the idea that hair follicles from certain areas, like the back and sides of the scalp, retain their resistance to baldness when transplanted elsewhere. Early techniques involved large grafts, often resulting in an unnatural appearance.",
      "In the 1990s, the advent of follicular unit transplantation (FUT) revolutionised the field. This technique involved transplanting small follicular units, improving the naturalness of results. Later, follicular unit extraction (FUE) emerged, allowing individual follicular units to be harvested directly from the scalp without a linear incision, reducing scarring.",
      "Advancements in technology, such as robotic assistance, better understanding of graft placement, and refined surgical skills, have made hair transplants more precise, yielding natural-looking results. Today, it's a widely practiced procedure, offering effective, permanent solutions to hair loss concerns.",
    ],
    milestones: [
      { year: "1952", label: "First modern hair transplant, Dr. Norman Orentreich" },
      { year: "1990s", label: "Follicular Unit Transplantation (FUT) introduced" },
      { year: "2000s", label: "Follicular Unit Extraction (FUE) emerges" },
      { year: "Today", label: "Robotic assistance & refined precision" },
    ],
  },

  indiaEvolution: {
    eyebrow: "In India",
    heading: "Evolution of Hair Transplant in India",
    paragraphs: [
      "The evolution of hair transplant in India has been remarkable. Initially introduced with traditional techniques like punch grafts, the field rapidly progressed. The 1990s saw the advent of follicular unit transplantation (FUT), enhancing naturalness and minimising scarring. As technology evolved, follicular unit extraction (FUE) emerged, revolutionising the procedure by extracting individual follicles without a linear scar.",
      "Advancements in robotics and automation further refined precision and efficiency. Indian clinics now offer state-of-the-art facilities, attracting global attention for high-quality yet affordable procedures. Surgeons continually refine techniques, incorporating PRP therapy, stem cells, and laser technology for improved outcomes.",
      "This evolution marked by enhanced precision, natural results, reduced downtime, and accessibility has positioned India as a hub for cutting-edge hair transplant procedures, catering not only to domestic but also international patients seeking world-class treatment at competitive costs.",
    ],
    link: { label: "Hair Transplant in Bangalore", href: "/hair-transplant-in-bangalore.php" },
  },

  benefits: {
    eyebrow: "Why It's Worth It",
    heading: "Benefits of Hair Transplant",
    intro:
      "Hair transplant procedures offer a transformative solution for individuals experiencing hair loss restoring not just hair, but confidence and self-assurance, with natural-looking results that last.",
    items: [
      { title: "Natural Appearance", desc: "Transplanted hair looks and feels natural, seamlessly blending with existing hair for an authentic look." },
      { title: "Permanent Solution", desc: "Offers a lasting solution to hair loss, as transplanted hair continues to grow naturally for a lifetime." },
      { title: "Boosts Self-Confidence", desc: "Restoring a fuller head of hair often leads to increased self-esteem and a more positive self-image." },
      { title: "Low Maintenance", desc: "Requires minimal ongoing care, similar to regular hair care routines, after the initial recovery period." },
      { title: "Cost-Effective", desc: "Compared to ongoing expenses on other hair loss treatments, a one-time transplant can be more economical in the long run." },
    ],
  },

  cta: {
    eyebrow: "Take the First Step",
    heading: "Ready to Learn More About Your Options?",
    sub: "Book a personalised consultation with our team and take the first step toward natural, lasting results.",
    primary: { label: "Book Consultation", href: "https://api.whatsapp.com/send?phone=919082123009&text=I%27m%20interested%20in%20Hair%20Restoration" },
    secondary: { label: "View All Treatments", href: "/male-hair-transplants.php" },
  },
};

/* ============================================================
   REUSABLE SUB-COMPONENTS
============================================================ */
function Eyebrow({ children, light = false, align = "left" }) {
  return (
    <p
      style={cinzel}
      className={`text-[10.5px] tracking-[0.28em] uppercase mb-3 flex items-center gap-2.5 ${
        align === "center" ? "justify-center" : "justify-start"
      } ${light ? "text-[#c9a84c]/70" : "text-[#a07828]"}`}
    >
      <span className={`w-6 h-px ${light ? "bg-[#c9a84c]/50" : "bg-[#c9a84c]"}`} />
      {children}
    </p>
  );
}

function SectionHeading({ children, light = false, align = "left" }) {
  return (
    <h2
      style={cinzel}
      className={`font-black text-[1.3rem] sm:text-[1.6rem] md:text-[1.85rem] uppercase tracking-[0.05em] mb-8 ${
        align === "center" ? "text-center" : "text-left"
      } ${light ? "text-white" : "text-[#1a0f02]"}`}
    >
      {children}
    </h2>
  );
}

function GoldDivider() {
  return <div className="w-full h-px bg-gradient-to-r from-transparent via-[#c9a84c]/30 to-transparent" />;
}

function FramedImage({ src, alt, className = "" }) {
  return (
    <div className={`relative rounded-2xl overflow-hidden border border-[#c9a84c]/25 ${className}`}>
      <img src={src} alt={alt} className="w-full h-full object-cover" />
      <span className="absolute inset-0 ring-1 ring-inset ring-[#c9a84c]/15 rounded-2xl pointer-events-none" />
    </div>
  );
}

/* ============================================================
   PAGE COMPONENT
============================================================ */
export default function AboutHairTransplantBangalore({ url = "https://bangalore.hairclinique.in/about-hair-transplants.php" }) {
  const pageRef = useRef(null);
  const heroEyeRef = useRef(null);
  const heroHeadRef = useRef(null);
  const heroSubRef = useRef(null);
  const sectionRefs = useRef([]);
  const addSection = (el) => el && !sectionRefs.current.includes(el) && sectionRefs.current.push(el);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const ease = "power3.out";
      gsap.fromTo(heroEyeRef.current, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.7, ease });
      gsap.fromTo(heroHeadRef.current, { opacity: 0, y: 26 }, { opacity: 1, y: 0, duration: 0.75, ease, delay: 0.1 });
      gsap.fromTo(heroSubRef.current, { opacity: 0, y: 22 }, { opacity: 1, y: 0, duration: 0.7, ease, delay: 0.18 });

      sectionRefs.current.forEach((el) => {
        if (!el) return;
        gsap.fromTo(
          el,
          { opacity: 0, y: 36 },
          {
            opacity: 1,
            y: 0,
            duration: 0.75,
            ease,
            scrollTrigger: { trigger: el, start: "top 88%", toggleActions: "play none none reverse" },
          }
        );
      });
    }, pageRef);
    return () => ctx.revert();
  }, []);

  /* full-bleed wrapper: fills the viewport with generous edge padding instead of a centered max-width column */
  const wrap = "w-full px-5 sm:px-10 lg:px-16 xl:px-24";
  const sec = "py-16 sm:py-20 md:py-24";

  return (
    <>
<SEO
  title="About Hair Transplant | Clinique Internationale"
  description="Learn about hair transplant procedures, techniques, and Sapphire FUE technology at Clinique Internationale — trusted expertise with Dr. Idris."
  url={url}
/>
      <link
        href="https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700;900&family=Raleway:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&display=swap"
        rel="stylesheet"
      />

     <div
  ref={pageRef}
  className="relative w-full bg-[#f2ece0] overflow-hidden mt-16 lg:mt-20"
  style={{ fontFamily: "'Raleway', sans-serif" }}
>
  {/* ══════════════════════════════════════
      HERO full width
  ══════════════════════════════════════ */}
  <div className="relative w-full h-[200px] xs:h-[280px] sm:h-[340px] md:h-[600px] flex items-center justify-end overflow-hidden">
    <img
      className="absolute inset-0 w-full h-full object-cover object-[center_20%] md:object-center"
      src={HeroImg}
      alt={PAGE.hero.heading}
    />
    <div className="absolute inset-0" />
    <div
      className="absolute inset-0 opacity-40 pointer-events-none"
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E\")",
      }}
    />
    <div className="relative z-10 text-right px-4 sm:px-10 md:px-16 max-w-[85%] sm:max-w-xl">
      <p
        ref={heroEyeRef}
        style={cinzel}
        className="text-[8px] xs:text-[9px] sm:text-[10.5px] tracking-[0.2em] xs:tracking-[0.24em] sm:tracking-[0.32em] uppercase text-[#c9a84c] mb-2 sm:mb-4 flex items-center justify-end gap-2 sm:gap-3"
      >
        <span className="w-4 sm:w-7 h-px bg-[#c9a84c]" />
        {PAGE.hero.eyebrow}
        <span className="w-4 sm:w-7 h-px bg-[#c9a84c]" />
      </p>
      <h1
        ref={heroHeadRef}
        style={cinzel}
        className="font-black text-[1.4rem] xs:text-[1.8rem] sm:text-[2.8rem] md:text-[3.4rem] tracking-[0.02em] sm:tracking-[0.04em] uppercase text-white mb-2 sm:mb-3 leading-[1.15] sm:leading-tight"
      >
        {PAGE.hero.heading}
      </h1>
      <p ref={heroSubRef} className="text-[11px] xs:text-[12px] sm:text-[15px] text-white/70 leading-relaxed ml-auto">
        {PAGE.hero.sub}
      </p>
    </div>
  </div>

        {/* ── BREADCRUMB full width with background image ── */}
        <div className="relative w-full overflow-hidden">
          <img className="absolute inset-0 w-full h-full object-cover" src={BreadcrumbImg} alt="" aria-hidden="true" />
          <div className="absolute inset-0 bg-[#0e0905]/88" />
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/25 to-transparent" />
          <div className={`relative ${wrap} py-3.5 flex flex-wrap items-center gap-2`}>
            {PAGE.breadcrumb.map((b, i) => (
              <span key={i} className="flex items-center gap-2">
                {i > 0 && <span className="text-[#c9a84c]/30 text-xs">›</span>}
                {b.href ? (
                  <a href={b.href} style={cinzel} className="text-[10px] tracking-widest uppercase text-[#c9a84c]/60 hover:text-[#c9a84c] transition-colors">
                    {b.label}
                  </a>
                ) : (
                  <span style={cinzel} className="text-[10px] tracking-widest uppercase text-[#c9a84c]">
                    {b.label}
                  </span>
                )}
              </span>
            ))}
          </div>
        </div>

        {/* ══════════════════════════════════════
            INTRO What is a Hair Transplant (text + image)
        ══════════════════════════════════════ */}
<div className={`${wrap} ${sec}`}>
          <div ref={addSection} className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
            <FramedImage src={ProcedureImg} alt="Hair transplant procedure close-up" className="h-[260px] sm:h-[320px] md:h-[380px] order-last md:order-first" />
            <div>
              <Eyebrow>{PAGE.intro.eyebrow}</Eyebrow>
              <SectionHeading>{PAGE.intro.heading}</SectionHeading>
              {PAGE.intro.paragraphs.map((p, i) => (
                <p key={i} className="text-[14.5px] leading-[1.9] text-[#3a2e1e] mb-4">
                  {p}
                </p>
              ))}
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════
            HISTORY (dark band, full width, image + milestones)
        ══════════════════════════════════════ */}
        <GoldDivider />
        <div className="bg-[#0e0905] py-16 sm:py-20 md:py-24">
          <div className={wrap}>
            <div ref={addSection} className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-start">
              <div>
                <Eyebrow light>{PAGE.history.eyebrow}</Eyebrow>
                <SectionHeading light>{PAGE.history.heading}</SectionHeading>
                {PAGE.history.paragraphs.map((p, i) => (
                  <p key={i} className="text-[13.5px] leading-[1.9] text-white/60 mb-4">
                    {p}
                  </p>
                ))}
              </div>
              <div className="flex flex-col gap-5">
                <FramedImage src={HistoryImg} alt="Hair transplant clinic through the decades" className="h-[220px] sm:h-[260px]" />
                <div className="grid grid-cols-2 gap-4">
                  {PAGE.history.milestones.map((m, i) => (
                    <div
                      key={i}
                      className="relative overflow-hidden bg-white/[0.04] border border-[#c9a84c]/15 rounded-2xl p-5 text-center hover:bg-[#c9a84c]/[0.07] hover:border-[#c9a84c]/30 transition-colors"
                    >
                      <span className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/40 to-transparent" />
                      <div
                        style={cinzel}
                        className="font-black text-[1.2rem] leading-none mb-2 bg-gradient-to-br from-[#c9943a] via-[#ffe9a0] to-[#c9943a] bg-clip-text text-transparent"
                      >
                        {m.year}
                      </div>
                      <div className="text-[11px] text-white/50 leading-snug">{m.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        <GoldDivider />

        {/* ══════════════════════════════════════
            EVOLUTION IN INDIA (text + image)
        ══════════════════════════════════════ */}
        <div className={`${wrap} ${sec}`}>
          <div ref={addSection} className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
            <FramedImage src={IndiaImg} alt="Modern hair transplant facility in India" className="h-[260px] sm:h-[320px] md:h-[380px]" />
            <div>
              <Eyebrow>{PAGE.indiaEvolution.eyebrow}</Eyebrow>
              <SectionHeading>{PAGE.indiaEvolution.heading}</SectionHeading>
              {PAGE.indiaEvolution.paragraphs.map((p, i) => (
                <p key={i} className="text-[14.5px] leading-[1.9] text-[#3a2e1e] mb-4">
                  {p}
                </p>
              ))}
              <a
                href={PAGE.indiaEvolution.link.href}
                style={cinzel}
                className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-[0.1em] uppercase text-[#a07828] hover:text-[#c9a84c] transition-colors mt-2"
              >
                {PAGE.indiaEvolution.link.label}
                <span>→</span>
              </a>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════
            BENEFITS
        ══════════════════════════════════════ */}
        <GoldDivider />
        <div className="bg-[#faf8f4] py-16 sm:py-20 md:py-24">
          <div className={wrap}>
            <div ref={addSection}>
              <Eyebrow align="center">{PAGE.benefits.eyebrow}</Eyebrow>
              <SectionHeading align="center">{PAGE.benefits.heading}</SectionHeading>
              <p className="text-[14.5px] leading-[1.9] text-[#3a2e1e] max-w-3xl mx-auto text-center mb-12">
                {PAGE.benefits.intro}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
                {PAGE.benefits.items.map((b, i) => (
                  <div
                    key={i}
                    className="bg-white border border-[#c9a84c]/18 rounded-2xl p-6 relative overflow-hidden hover:-translate-y-1.5 hover:shadow-[0_12px_32px_rgba(90,60,10,0.1)] transition-all duration-300"
                  >
                    <span className="absolute top-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/40 to-transparent" />
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#c9a84c]/10 to-[#c9a84c]/[0.03] border border-[#c9a84c]/20 flex items-center justify-center mb-4">
                      <span style={cinzel} className="text-[12px] font-black text-[#c9a84c]">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <div style={cinzel} className="text-[12.5px] font-bold tracking-[0.06em] uppercase text-[#1a0f02] mb-2">
                      {b.title}
                    </div>
                    <p className="text-[13px] leading-[1.75] text-[#6b5535]">{b.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <GoldDivider />

        {/* ══════════════════════════════════════
            CTA
        ══════════════════════════════════════ */}
        <div className={`${wrap} ${sec}`}>
          <div ref={addSection} className="max-w-xl mx-auto text-center">
            <Eyebrow align="center">{PAGE.cta.eyebrow}</Eyebrow>
            <h3
              style={cinzel}
              className="font-black text-[1.3rem] sm:text-[1.65rem] md:text-[1.9rem] text-[#1a0f02] uppercase tracking-[0.06em] mb-3.5"
            >
              {PAGE.cta.heading}
            </h3>
            <p className="text-[14px] text-[#6b5535] leading-[1.75] mb-8">{PAGE.cta.sub}</p>
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <a
                href={PAGE.cta.primary.href}
                target="_blank"
                rel="noopener noreferrer"
                style={{ ...cinzel, backgroundImage: "linear-gradient(135deg,#c9943a,#ffe9a0,#c9943a)", backgroundSize: "200% 100%" }}
                className="text-[11px] font-bold tracking-[0.16em] uppercase px-8 py-3.5 rounded-md text-[#1a0f02] hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(201,168,76,0.35)] transition-all inline-block"
              >
                {PAGE.cta.primary.label}
              </a>
              <a
                href={PAGE.cta.secondary.href}
                style={cinzel}
                className="text-[11px] font-bold tracking-[0.16em] uppercase px-8 py-[13px] rounded-md bg-transparent border border-[#1a0f02]/35 text-[#1a0f02] hover:-translate-y-0.5 hover:border-[#c9a84c] hover:text-[#a07828] transition-all inline-block"
              >
                {PAGE.cta.secondary.label}
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}